import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Table } from 'antd'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { fetchAccountInvestmentMeasures } from '../../../services/companiesService.ts'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'
import {
  InvestmentMeasureRecord,
  InvestmentMeasureRecordFields
} from '../../../../../state_manager/data_models/investment_measure_record.ts'
import type { ColumnsType } from 'antd/es/table'

interface InvestmentSummarySectionProps {
  accountId: string | null
  onRefreshRequest?: (refreshFn: () => Promise<void>) => void
  isDrawer?: boolean
}

export function InvestmentSummarySection({
  accountId,
  onRefreshRequest,
  isDrawer = false
}: InvestmentSummarySectionProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [measures, setMeasures] = useState<InvestmentMeasureRecord[]>([])
  const [totalRecord, setTotalRecord] = useState<InvestmentMeasureRecord | null>(null)

  // Clear data immediately when accountId changes, then fetch new data
  useEffect(() => {
    // Clear data immediately when accountId changes to prevent showing stale data
    setMeasures([])
    setTotalRecord(null)
    setError(null)
    setLoading(true)

    const fetchData = async () => {
      if (!accountId) {
        setMeasures([])
        setTotalRecord(null)
        setLoading(false)
        return
      }

      try {
        const currentToken = getAccessToken(store.getState().auth)
        if (!currentToken) {
          setError('No access token available')
          setLoading(false)
          return
        }

        const response = await fetchAccountInvestmentMeasures(accountId, currentToken)

        if (response.error) {
          setError(response.error)
          setMeasures([])
          setTotalRecord(null)
        } else {
          // Separate "Total" entry from regular entries
          const totalEntry = response.data.find(
            (record: InvestmentMeasureRecord) => 
              record[InvestmentMeasureRecordFields.funds__name] === 'Total'
          )
          const regularEntries = response.data.filter(
            (record: InvestmentMeasureRecord) => 
              record[InvestmentMeasureRecordFields.funds__name] !== 'Total'
          )

          // Sort regular entries by date_invested ascending
          const sortedMeasures = [...regularEntries].sort((a, b) => {
            const dateA = a[InvestmentMeasureRecordFields.date_invested] || ''
            const dateB = b[InvestmentMeasureRecordFields.date_invested] || ''
            return dateA.localeCompare(dateB)
          })
          
          setMeasures(sortedMeasures)
          setTotalRecord(totalEntry || null)
          setError(null)
        }
      } catch (err) {
        console.error('Error fetching investment measures:', err)
        setError(err instanceof Error ? err.message : 'Failed to fetch investment measures')
        setMeasures([])
        setTotalRecord(null)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [accountId])

  const handleRefresh = async () => {
    if (!accountId) return

    // Clear data immediately before refreshing to prevent showing stale data
    setMeasures([])
    setTotalRecord(null)
    setError(null)
    setLoading(true)

    try {
      const currentToken = getAccessToken(store.getState().auth)
      if (!currentToken) {
        setError('No access token available')
        setLoading(false)
        return
      }

      const response = await fetchAccountInvestmentMeasures(accountId, currentToken)

      if (response.error) {
        setError(response.error)
        setMeasures([])
        setTotalRecord(null)
      } else {
        // Separate "Total" entry from regular entries
        const totalEntry = response.data.find(
          (record: InvestmentMeasureRecord) => 
            record[InvestmentMeasureRecordFields.funds__name] === 'Total'
        )
        const regularEntries = response.data.filter(
          (record: InvestmentMeasureRecord) => 
            record[InvestmentMeasureRecordFields.funds__name] !== 'Total'
        )

        // Sort regular entries by date_invested ascending
        const sortedMeasures = [...regularEntries].sort((a, b) => {
          const dateA = a[InvestmentMeasureRecordFields.date_invested] || ''
          const dateB = b[InvestmentMeasureRecordFields.date_invested] || ''
          return dateA.localeCompare(dateB)
        })
        
        setMeasures(sortedMeasures)
        setTotalRecord(totalEntry || null)
        setError(null)
      }
    } catch (err) {
      console.error('Error refreshing investment measures:', err)
      setError(err instanceof Error ? err.message : 'Failed to refresh investment measures')
      setMeasures([])
      setTotalRecord(null)
    } finally {
      setLoading(false)
    }
  }

  const refreshRef = useRef<(() => Promise<void>) | null>(null)
  refreshRef.current = handleRefresh

  useEffect(() => {
    if (onRefreshRequest && refreshRef.current) {
      onRefreshRequest(refreshRef.current)
    }
  }, [onRefreshRequest])

  const columns: ColumnsType<InvestmentMeasureRecord> = useMemo(() => {
    if (isDrawer) {
      return [
        {
          title: 'Fund',
          dataIndex: InvestmentMeasureRecordFields.funds__name,
          key: 'fund',
          render: (text: string | null | undefined) => text || '-'
        },
        {
          title: 'Invested',
          dataIndex: InvestmentMeasureRecordFields.cost_incurred_fmt,
          key: 'invested',
          align: 'right' as const,
          render: (text: string | null | undefined) => text || '-'
        },
        {
          title: 'Realized Value',
          dataIndex: InvestmentMeasureRecordFields.proceeds_fmt,
          key: 'realized',
          align: 'right' as const,
          render: (text: string | null | undefined) => text || '-'
        },
        {
          title: 'Unrealized Value',
          dataIndex: InvestmentMeasureRecordFields.fmv_fmt,
          key: 'unrealized',
          align: 'right' as const,
          render: (text: string | null | undefined) => text || '-'
        },
        {
          title: 'MOIC',
          dataIndex: InvestmentMeasureRecordFields.moic_fmt,
          key: 'moic',
          align: 'right' as const,
          render: (text: string | null | undefined) => text || '-'
        }
      ]
    } else {
      return [
        {
          title: 'Fund',
          dataIndex: InvestmentMeasureRecordFields.funds__name,
          key: 'fund',
          render: (text: string | null | undefined) => text || '-'
        },
        {
          title: 'First Invested',
          dataIndex: InvestmentMeasureRecordFields.date_invested_fmt,
          key: 'first_invested',
          render: (text: string | null | undefined) => text || '-'
        },
        {
          title: 'Invested',
          dataIndex: InvestmentMeasureRecordFields.cost_incurred_fmt,
          key: 'invested',
          align: 'right' as const,
          render: (text: string | null | undefined) => text || '-'
        },
        {
          title: 'Cost Basis',
          dataIndex: InvestmentMeasureRecordFields.cost_fmt,
          key: 'cost_basis',
          align: 'right' as const,
          render: (text: string | null | undefined) => text || '-'
        },
        {
          title: 'Realized Value',
          dataIndex: InvestmentMeasureRecordFields.proceeds_fmt,
          key: 'realized',
          align: 'right' as const,
          render: (text: string | null | undefined) => text || '-'
        },
        {
          title: 'Unrealized Value',
          dataIndex: InvestmentMeasureRecordFields.fmv_fmt,
          key: 'unrealized',
          align: 'right' as const,
          render: (text: string | null | undefined) => text || '-'
        },
        {
          title: 'MOIC',
          dataIndex: InvestmentMeasureRecordFields.moic_fmt,
          key: 'moic',
          align: 'right' as const,
          render: (text: string | null | undefined) => text || '-'
        },
        {
          title: 'FMV P/S',
          dataIndex: InvestmentMeasureRecordFields.unit_price_fmt,
          key: 'unit_price',
          align: 'right' as const,
          render: (text: string | null | undefined) => text || '-'
        },
        {
          title: 'No. Held',
          dataIndex: InvestmentMeasureRecordFields.no_held_fmt,
          key: 'no_held',
          align: 'right' as const,
          render: (text: string | null | undefined) => text || '-'
        },
        {
          title: 'Principal Held',
          dataIndex: InvestmentMeasureRecordFields.principal_held_fmt,
          key: 'principal_held',
          align: 'right' as const,
          render: (text: string | null | undefined) => text || '-'
        }
      ]
    }
  }, [isDrawer])

  // Render totals summary using the "Total" entry from API
  const renderSummary = () => {
    if (!totalRecord) {
      // Hide totals when no total record available
      return null
    }

    if (isDrawer) {
      return (
        <Table.Summary fixed>
          <Table.Summary.Row style={{ backgroundColor: '#f9fafb', fontWeight: 'bold' }}>
            <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
              {totalRecord[InvestmentMeasureRecordFields.cost_incurred_fmt] || '-'}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right">
              {totalRecord[InvestmentMeasureRecordFields.proceeds_fmt] || '-'}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={3} align="right">
              {totalRecord[InvestmentMeasureRecordFields.fmv_fmt] || '-'}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={4} align="right">
              {totalRecord[InvestmentMeasureRecordFields.moic_fmt] || '-'}
            </Table.Summary.Cell>
          </Table.Summary.Row>
        </Table.Summary>
      )
    } else {
      return (
        <Table.Summary fixed>
          <Table.Summary.Row style={{ backgroundColor: '#f9fafb', fontWeight: 'bold' }}>
            <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
            <Table.Summary.Cell index={1}></Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right">
              {totalRecord[InvestmentMeasureRecordFields.cost_incurred_fmt] || '-'}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={3} align="right">
              {totalRecord[InvestmentMeasureRecordFields.cost_fmt] || '-'}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={4} align="right">
              {totalRecord[InvestmentMeasureRecordFields.proceeds_fmt] || '-'}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={5} align="right">
              {totalRecord[InvestmentMeasureRecordFields.fmv_fmt] || '-'}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={6} align="right">
              {totalRecord[InvestmentMeasureRecordFields.moic_fmt] || '-'}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={7} align="right">
              {totalRecord[InvestmentMeasureRecordFields.unit_price_fmt] || '-'}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={8} align="right">
              {totalRecord[InvestmentMeasureRecordFields.no_held_fmt] || '-'}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={9} align="right">
              {totalRecord[InvestmentMeasureRecordFields.principal_held_fmt] || '-'}
            </Table.Summary.Cell>
          </Table.Summary.Row>
        </Table.Summary>
      )
    }
  }

  return (
    <FormSection
      data-debug='investment-summary-section'
      data-component='InvestmentSummarySection'
      title='Investment Summary'
      titleColor='blue'
      className='shadow-sm border border-gray-200'
    >
      {error ? (
        <div style={{ color: 'red', padding: '10px' }}>{error}</div>
      ) : (
        <>
          <style>
            {`
              .investment-summary-empty-table .ant-table-empty .ant-table-placeholder {
                min-height: 50% !important;
                padding: 16px 0 !important;
              }
              .investment-summary-empty-table .ant-table-empty .ant-table-tbody {
                min-height: 50% !important;
              }
            `}
          </style>
          <div className={measures.length === 0 ? 'investment-summary-empty-table' : ''}>
            <Table
              loading={loading}
              dataSource={measures}
              columns={columns}
              rowKey={(record, index) => {
                // Use fund name + index as key since we don't have a unique ID
                const fundName = record[InvestmentMeasureRecordFields.funds__name] || ''
                return `${fundName}-${index}`
              }}
              pagination={false}
              size='small'
              scroll={{ x: 'max-content' }}
              summary={renderSummary}
              showHeader={measures.length > 0}
              locale={{
                emptyText: <div style={{ padding: '16px 0' }}>No investment data</div>
              }}
            />
          </div>
        </>
      )}
    </FormSection>
  )
}
